import { useEffect, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { ArrowRight, Loader2 } from "lucide-react";

export const RescheduleDialog = ({ appt, patientId, onClose, onDone }) => {
  const [date, setDate] = useState("");
  const [slots, setSlots] = useState([]);
  const [loading, setLoading] = useState(false);
  const [picked, setPicked] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!appt) return;
    setPicked(null);
    setLoading(true);
    const params = { business_id: appt.business_id, practitioner_id: appt.practitioner_id, appointment_type_id: appt.type_id };
    if (date) params.date = date;
    api.get("/admin/slots", { params })
      .then((r) => setSlots(r.data.slots || []))
      .catch((e) => { setSlots([]); toast.error(e.response?.data?.detail || "Could not load free times"); })
      .finally(() => setLoading(false));
  }, [appt, date]);

  const confirm = async () => {
    setBusy(true);
    try {
      await api.post("/admin/reschedule", {
        cliniko_id: patientId, appointment_id: appt.appt_id, appointment_type_id: appt.type_id,
        business_id: appt.business_id, practitioner_id: appt.practitioner_id, new_start: picked.start,
        from_label: appt.label, to_label: picked.label,
      });
      toast.success(`Moved to ${picked.label}`);
      onDone();
    } catch (e) { toast.error(e.response?.data?.detail || "Reschedule failed — nothing was changed"); }
    finally { setBusy(false); }
  };

  return (
    <Dialog open={!!appt} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-lg bg-white" data-testid="reschedule-dialog">
        <DialogHeader>
          <DialogTitle>Reschedule appointment</DialogTitle>
          <DialogDescription>Currently: {appt?.label}</DialogDescription>
        </DialogHeader>
        {!picked ? (
          <div className="space-y-3">
            <Input type="date" value={date} onChange={(e) => setDate(e.target.value)} data-testid="reschedule-date" />
            <p className="text-[11px] text-slate-400">{date ? "Free times on this day" : "Next free times (pick a date to narrow down)"}</p>
            {loading ? <Loader2 className="h-5 w-5 animate-spin text-teal-600" /> : (
              <div className="grid grid-cols-2 gap-2" data-testid="reschedule-slots">
                {slots.length === 0 && <p className="col-span-2 text-sm text-slate-500" data-testid="reschedule-no-slots">No free times found — try another date.</p>}
                {slots.map((s) => (
                  <button key={s.start} onClick={() => setPicked(s)} data-testid={`reschedule-slot-${s.start}`}
                    className="rounded-lg border border-slate-200 px-3 py-2 text-left text-sm transition-colors hover:border-teal-500 hover:bg-teal-50">{s.label}</button>
                ))}
              </div>
            )}
          </div>
        ) : (
          <div className="space-y-4" data-testid="reschedule-confirm">
            <div className="flex flex-wrap items-center gap-3 rounded-xl bg-slate-50 p-4 text-sm">
              <span className="text-slate-500 line-through" data-testid="reschedule-old-time">{appt?.label}</span>
              <ArrowRight className="h-4 w-4 text-teal-600" />
              <span className="font-semibold text-slate-900" data-testid="reschedule-new-time">{picked.label}</span>
            </div>
            <p className="text-xs text-slate-500">This books the new time in Cliniko, then cancels the old one.</p>
            <div className="flex justify-end gap-2">
              <Button variant="outline" onClick={() => setPicked(null)} data-testid="reschedule-back">Back</Button>
              <Button onClick={confirm} disabled={busy} className="bg-teal-600 hover:bg-teal-700" data-testid="reschedule-confirm-btn">
                {busy && <Loader2 className="mr-1 h-4 w-4 animate-spin" />} Confirm reschedule
              </Button>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};
